/**
 * QueryClient do app.
 *
 * Erro de rede em GET não avisa no interceptor do axios (ver `api.ts`): quem
 * avisa é o `QueryCache`, e só quando as duas condições valem juntas:
 *   1. os retries acabaram — a queda não foi momentânea;
 *   2. a consulta não tinha dado nenhum — a tela está vazia.
 * Refetch em background (polling, invalidação na reconexão do socket) de uma
 * tela que já mostra dado fica em silêncio: o próximo tick corrige sozinho.
 *
 * Erro com resposta do servidor não passa por aqui: o interceptor já avisou
 * com a mensagem do backend, e avisar de novo seria toast duplicado.
 */
import { QueryCache, QueryClient } from '@tanstack/react-query';

import { isNetworkError, notifyNetworkError } from './network-error';

export const createQueryClient = (): QueryClient =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (isNetworkError(error) && query.state.data === undefined) {
          notifyNetworkError();
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        retry: (failureCount, error: unknown) => {
          const status = (error as { response?: { status?: number } } | null)?.response?.status;
          // Não retentar em erros de permissão (403) ou não encontrado (404)
          if (status === 403 || status === 404) {
            return false;
          }
          // Retentar até 2 vezes para outros erros (network, 500, etc.)
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
      },
    },
  });
